import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { Button } from '@/components/Button';
import { CheckCircle, Cross, Warning } from '@/components/icons';
import { Leaving } from '@/components/Leaving';

export type ToastTone = 'success' | 'error' | 'info';

export interface Toast {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string;
}

interface ToastApi {
  push: (toast: Omit<Toast, 'id'>) => void;
}

/** A toast in the stack, and whether it is on its way out. */
interface Entry extends Toast {
  leaving: boolean;
}

const ToastContext = createContext<ToastApi | null>(null);

function lifetime(tone: ToastTone): number {
  return tone === 'error' ? 9000 : 5000;
}

/** After the pointer leaves the stack, held toasts get this long before they go. */
const GRACE_MS = 2000;

/**
 * Toasts rise in at the bottom of the window and, when dismissed or timed out, fade and
 * give up their height so the stack closes ranks. Hovering or focusing the stack holds
 * every toast until the pointer leaves, so a message can be read or its link copied.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Entry[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, number>());
  const held = useRef(false);
  const current = useRef<Entry[]>([]);
  useEffect(() => {
    current.current = toasts;
  }, [toasts]);

  const remove = useCallback(
    (id: number) => setToasts((list) => list.filter((t) => t.id !== id)),
    [],
  );

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) window.clearTimeout(timer);
    timers.current.delete(id);
    setToasts((list) => list.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
  }, []);

  const arm = useCallback(
    (id: number, ms: number) => {
      const existing = timers.current.get(id);
      if (existing !== undefined) window.clearTimeout(existing);
      timers.current.set(
        id,
        window.setTimeout(() => {
          timers.current.delete(id);
          dismiss(id);
        }, ms),
      );
    },
    [dismiss],
  );

  const push = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = nextId.current++;
      setToasts((list) => [...list, { ...toast, id, leaving: false }]);
      if (!held.current) arm(id, lifetime(toast.tone));
    },
    [arm],
  );

  const hold = useCallback(() => {
    held.current = true;
    for (const timer of timers.current.values()) window.clearTimeout(timer);
    timers.current.clear();
  }, []);

  const release = useCallback(() => {
    if (!held.current) return;
    held.current = false;
    for (const toast of current.current) if (!toast.leaving) arm(toast.id, GRACE_MS);
  }, [arm]);

  // A toast that leaves under the pointer takes its pointerleave with it: an empty stack is
  // never held.
  useEffect(() => {
    if (toasts.length === 0) held.current = false;
  }, [toasts.length]);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) window.clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const api = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Each toast is its own live region (status, or alert for a failure). */}
      <div
        className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center px-4"
        onPointerEnter={hold}
        onPointerLeave={release}
        onFocusCapture={hold}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) release();
        }}
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} onLeft={remove} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({
  toast,
  onDismiss,
  onLeft,
}: {
  toast: Entry;
  onDismiss: (id: number) => void;
  onLeft: (id: number) => void;
}) {
  return (
    <Leaving
      id={toast.id}
      leaving={toast.leaving}
      onLeft={onLeft}
      className="w-full max-w-[460px] pt-2"
    >
      <div>
        <div
          role={toast.tone === 'error' ? 'alert' : 'status'}
          className={[
            'pointer-events-auto flex w-full items-start gap-3 rounded-lg border bg-surface px-4 py-3 shadow-pop text-body animate-rise',
            toast.tone === 'error'
              ? 'border-danger/40'
              : toast.tone === 'success'
                ? 'border-status-ok/40'
                : 'border-line',
          ].join(' ')}
        >
          <span
            className={
              toast.tone === 'error'
                ? 'text-danger-ink mt-0.5'
                : toast.tone === 'success'
                  ? 'text-status-ok-ink mt-0.5'
                  : 'text-brand mt-0.5'
            }
          >
            {toast.tone === 'error' ? <Warning size={16} /> : <CheckCircle size={16} />}
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-ink">{toast.title}</p>
            {toast.description ? (
              <p className="text-ink-2 mt-0.5 break-words">{toast.description}</p>
            ) : null}
          </div>
          <Button
            variant="ghost"
            size="sm"
            aria-label="Dismiss"
            onClick={() => onDismiss(toast.id)}
            className="-mr-2 px-1.5"
          >
            <Cross size={14} />
          </Button>
        </div>
      </div>
    </Leaving>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be used inside <ToastProvider>.');
  return api;
}
